import { AxiosInstance } from 'axios';
import { BaseApi } from 'services/api/base-api';
import { ApiResult } from 'services/api/result.type';
import {
  CreatePathwayRequest,
  EligibilityPreviewRequest,
  EligibilityPreviewResponse,
  ListPathwaySchoolEventsResponse,
  ListPathwayStudentsResponse,
  ListPathwaysResponse,
  PathwayDetail,
  UpdatePathwayRequest,
} from './dto/pathways.dto';

export class PathwaysApi extends BaseApi {
  public constructor (protected readonly api: AxiosInstance) {
    super();

    this.listPathways = this.listPathways.bind(this);
    this.getPathway = this.getPathway.bind(this);
    this.createPathway = this.createPathway.bind(this);
    this.updatePathway = this.updatePathway.bind(this);
    this.publishPathway = this.publishPathway.bind(this);
    this.archivePathway = this.archivePathway.bind(this);
    this.addEvents = this.addEvents.bind(this);
    this.removeEvent = this.removeEvent.bind(this);
    this.addEligibleOrganizations = this.addEligibleOrganizations.bind(this);
    this.removeEligibleOrganization =
      this.removeEligibleOrganization.bind(this);
    this.eligibilityPreview = this.eligibilityPreview.bind(this);
    this.listPathwayStudents = this.listPathwayStudents.bind(this);
    this.listSchoolEvents = this.listSchoolEvents.bind(this);
  }

  public async listPathways (params?: {
    search?: string | null;
    includeArchived?: boolean;
  }): ApiResult<ListPathwaysResponse> {
    return await this.get<ListPathwaysResponse>('/pathways', { params });
  }

  public async getPathway (id: string): ApiResult<PathwayDetail> {
    return await this.get<PathwayDetail>(`/pathways/${id}`);
  }

  public async createPathway (
    body: CreatePathwayRequest,
  ): ApiResult<{ id: string }> {
    return await this.post<{ id: string }>('/pathways', body);
  }

  public async updatePathway (
    id: string,
    body: UpdatePathwayRequest,
  ): ApiResult<void> {
    return await this.patch<void>(`/pathways/${id}`, body);
  }

  public async publishPathway (id: string): ApiResult<void> {
    return await this.post<void>(`/pathways/${id}/publish`);
  }

  public async archivePathway (id: string): ApiResult<void> {
    return await this.post<void>(`/pathways/${id}/archive`);
  }

  public async addEvents (
    id: string,
    eventIds: string[],
  ): ApiResult<void> {
    return await this.post<void>(`/pathways/${id}/events`, { eventIds });
  }

  public async removeEvent (
    id: string,
    eventId: string,
  ): ApiResult<void> {
    return await this.delete<void>(`/pathways/${id}/events/${eventId}`);
  }

  public async addEligibleOrganizations (
    id: string,
    organizationIds: string[],
  ): ApiResult<void> {
    return await this.post<void>(`/pathways/${id}/eligible-organizations`, {
      organizationIds,
    });
  }

  public async removeEligibleOrganization (
    id: string,
    organizationId: string,
  ): ApiResult<void> {
    return await this.delete<void>(
      `/pathways/${id}/eligible-organizations/${organizationId}`,
    );
  }

  public async eligibilityPreview (
    body: EligibilityPreviewRequest,
  ): ApiResult<EligibilityPreviewResponse> {
    return await this.post<EligibilityPreviewResponse>(
      '/pathways/eligibility-preview',
      body,
    );
  }

  public async listPathwayStudents (
    id: string,
    params?: { search?: string | null; page?: number; pageSize?: number },
  ): ApiResult<ListPathwayStudentsResponse> {
    return await this.get<ListPathwayStudentsResponse>(
      `/pathways/${id}/students`,
      { params },
    );
  }

  public async listSchoolEvents (params?: {
    search?: string | null;
    organizationId?: string | null;
    page?: number;
    pageSize?: number;
  }): ApiResult<ListPathwaySchoolEventsResponse> {
    return await this.get<ListPathwaySchoolEventsResponse>('/pathways/events', {
      params,
    });
  }
}
